import { apiClient } from '../core/api/apiClient';

export interface MotivationScoreEntry {
  score: string | number;
  reasoning?: string;
  supporting_signals?: string;
}

export interface AlumniProfile {
  _id: string;
  donor_id?: string;
  is_alumni?: boolean;
  source_info?: {
    csv_name?: string;
    current_name?: string;
    alumni_id?: string;
    alumni_csr_id?: string;
    donor_id?: string;
    designation?: string;
    company?: string;
    scraped_at: string;
    degree?: string;
    batch?: string;
    department?: string;
    work_city?: string;
    person_account_work_address_country_territory?: string;
    potential_donor?: boolean;
    anonymous_donor?: boolean;
    cumulative_giving_to_iitm?: number;
    salesforce_url?: string;
    salesforce_link?: string;
    linked_in?: string;
    last_touch?: string;
    largest_gift_to_iitm?: string | number;
    date_of_first_gift_to_iitm?: string;
    date_of_last_gift_to_iitm?: string;
    total_number_of_payments?: number;
    last_cause_supported?: string;
    smallest_gift?: string | number;
    account_record_type?: string;
    account_leader?: string;
    sales_person?: string;
    account_18_digit_id?: string;
    gender?: string;
    daa?: boolean;
    hni?: boolean;
    mailing_city?: string;
    mailing_state_province?: string;
    mailing_country?: string;
    work_address_city?: string;
    headline?: string;
    [key: string]: any;
  };
  linkedin_scraped_at?: string;
  linkedin_profile?: {
    name?: string;
    location?: string;
    city?: string;
    url?: string;
    experience?: Array<{
      title?: string;
      company?: string;
      starts_at?: { year?: number; month?: number; day?: number };
      ends_at?: { year?: number; month?: number; day?: number } | null;
      description?: string;
      location?: string;
    }>;
    education?: Array<{
      degree?: string;
      school?: string;
      field_of_study?: string;
      starts_at?: { year?: number; month?: number; day?: number };
      ends_at?: { year?: number; month?: number; day?: number } | null;
      description?: string;
    }>;
  };
  linkedin_info?: {
    name?: string;
    location?: string;
    city?: string;
    url?: string;
    avatar?: string;
    photo?: string;
    profilePic?: string;
    profile_picture?: string;
    photo_url?: string;
    position?: string;
    current_company_name?: string;
    connections?: number;
    experience?: Array<{
      title?: string;
      company?: string;
      start_date?: string;
      end_date?: string;
      description?: string;
    }>;
    education?: Array<{
      degree?: string;
      school?: string;
      start_date?: string;
      end_date?: string;
    }>;
    about?: string;
    skills?: Array<{
      name?: string;
      endorsements?: string;
    }>;
    volunteering?: Array<{
      role?: string;
      duration?: string;
      startDate?: string;
      endDate?: string;
      organizationName?: string;
      organizationLinkedinUrl?: string;
      cause?: string;
    }>;
    languages?: Array<{
      title?: string;
      subtitle?: string;
    }>;
    projects?: Array<{
      title?: string;
      start_date?: string;
      end_date?: string;
      description?: string;
    }>;
    certifications?: Array<{
      title?: string;
      subtitle?: string;
      meta?: string;
      credential_url?: string;
      credential_id?: string;
    }>;
    publications?: Array<{
      title?: string;
      description?: string;
      date?: string;
      publisher?: string;
      url?: string;
    }>;
    patents?: Array<{
      title?: string;
      date_issued?: string;
      patents_id?: string;
      description?: string;
    }>;
    honors_and_awards?: Array<{
      title?: string;
      publication?: string;
      date?: string;
      description?: string;
    }>;
    honorsAndAwards?: Array<{
      title?: string;
      issuedBy?: string;
      issuedAt?: string;
      description?: string;
      associatedWith?: string;
      associatedWithLink?: string;
    }>;
    organizations?: Array<{
      title?: string;
      membership_type?: string;
      start_date?: string;
      end_date?: string;
      membership_number?: string;
    }>;
    courses?: Array<{
      title?: string;
      subtitle?: string;
    }>;
    activity?: Array<{
      interaction?: string;
      link?: string;
      title?: string;
      img?: string;
      id?: string;
    }>;
    people_also_viewed?: Array<{
      profile_link?: string;
      name?: string;
      about?: string;
      location?: string;
    }>;
  };
  monday_data?: Array<{
    payment_id?: string;
    project?: string;
    date?: string | number | Record<string, unknown>;
    currency?: string;
    amount?: string | number;
    paid?: boolean;
    donation_through?: string;
    type?: string;
    salesforce_object?: string;
    cause_id?: string;
    last_modified_date?: string | Date;
  }>;
  ai_research?: {
    citations?: string[];
    search_mode?: string;
    researched_at?: string;
    researched_by?: string;
    person_snapshot?: Record<string, unknown>;
    structured_data?: {
      brief?: string;
      company?: string;
      designation?: string;
      philanthropic_interests?: string;
      interests?: string;
      wealth_signals?: string;
      awards_and_recognition?: string;
      media_and_profiles?: string;
      board_and_advisory?: string;
      estimated_donation_capacity?: string;
      estimated_donation_capacity_reasoning?: string;
      career_trajectory?: {
        current_position?: string;
        trajectory_direction?: string;
        cxo_potential?: string;
        founder_potential?: string;
        predicted_roles?: string[];
        key_signals?: string[];
        industry_outlook?: string;
        future_wealth_impact?: string;
        confidence?: 'High' | 'Medium' | 'Low' | 'Not found';
      };
      readiness_to_give?: 'High' | 'Medium' | 'Low' | 'Not found';
      alumni_interest_at_iit?: 'High' | 'Medium' | 'Low' | 'Not found';
      reasoning?: string;
      potential_donor_score?: number | string;
      strategic_fit_score?: string;
      targeted_donation_amount?: string;
      targeted_donation_amount_reasoning?: string;
      current_earning?: string;
      approach_for_cause?: string;
      research_quality?: 'High' | 'Medium' | 'Low';
      data_correction_notes?: string;
      cash_earnings?: { amount?: string; source?: string; note?: string };
      stock_holdings?: { amount?: string; source?: string; note?: string; validation_trace?: string[] };
      motivation_scores?: {
        iitm_affinity_alumni_identity?: MotivationScoreEntry;
        research_knowledge_advancement?: MotivationScoreEntry;
        founder_innovation_ecosystem?: MotivationScoreEntry;
        legacy_recognition?: MotivationScoreEntry;
        student_talent_development?: MotivationScoreEntry;
        social_national_impact?: MotivationScoreEntry;
      };
      top_donation_motivation?: { category?: string; score?: string | number; reasoning?: string };
    };
    report?: string;
  };
  vals_framework?: Record<string, unknown>;
  name?: string;
  similarity_breakdown?: {
    vals: number;
    interest: number;
    wealth: number;
  };
  composite_score?: number;
  last_modified_date?: string | Date;
  /** Email of whoever made the last change; null for changes made before this was recorded. */
  last_modified_by?: string | null;
  /** What kind of change it was: import, donations, added, linkedin_edit, linkedin_scrape, vals, ai_research. */
  last_modified_source?: string;
  manual_linkedin?: {
    url: string;
    entered_by: string;
    entered_at: string;
  };
  linkedin_overwrite_flag?: boolean;
  research_locked?: boolean;
}

export interface AlumniListResponse {
  alumni: AlumniProfile[];
  pages: number;
  total: number;
}

export const alumniService = {
  getAlumniList: async (options: {
    page?: number;
    limit?: number;
    q?: string;
    batch?: string;
    department?: string;
    location?: string;
    industry?: string;
    research_locked?: boolean;
    sort_by?: string;
    sort_order?: number;
  } = {}): Promise<AlumniListResponse> => {
    return apiClient<AlumniListResponse>('/dashboard/alumni', {
      method: 'GET',
      params: options,
    });
  },
  getAlumniById: async (alumniId: string): Promise<AlumniProfile> => {
    return apiClient<AlumniProfile>(`/dashboard/alumni/${alumniId}`, {
      method: 'GET',
    });
  },

  getSimilarAlumni: async (alumniId: string, limit?: number): Promise<AlumniProfile[]> => {
    return apiClient<AlumniProfile[]>(`/dashboard/alumni/${alumniId}/similar`, {
      method: 'GET',
      params: limit ? { limit } : undefined,
    });
  },
  createAlumniProfile: async (data: { is_alumni: boolean; donor_id?: string; source_info: any }): Promise<{ status: string, donor_id: string, message: string }> => {
    return apiClient<{ status: string, donor_id: string, message: string }>('/dashboard/alumni', {
      method: 'POST',
      data,
    });
  },
  updateLinkedinPath: async (alumniId: string, linkedinUrl: string): Promise<AlumniProfile> => {
    return apiClient<AlumniProfile>(`/dashboard/alumni/${alumniId}/linkedin-path`, {
      method: 'PUT',
      data: { linkedin_url: linkedinUrl },
    });
  },
  clearLinkedinFlag: async (alumniId: string, revertToManual: boolean): Promise<AlumniProfile> => {
    return apiClient<AlumniProfile>(`/dashboard/alumni/${alumniId}/linkedin-flag`, {
      method: 'DELETE',
      data: { revert_to_manual: revertToManual },
    });
  },
  exportLinkedinOverrides: async (): Promise<Blob> => {
    return apiClient<Blob>('/dashboard/alumni/linkedin-overrides/export', {
      method: 'GET',
      responseType: 'blob',
    });
  },
  toggleResearchLock: async (alumniId: string, locked: boolean): Promise<{ status: string; research_locked: boolean }> => {
    return apiClient<{ status: string; research_locked: boolean }>(`/dashboard/alumni/${alumniId}/research-lock`, {
      method: 'PUT',
      data: { locked },
    });
  },
};


